import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import './notifications.scss';
import { Link, useHistory } from 'react-router-dom';
import { Image, Label } from 'semantic-ui-react';
import moment from 'moment';
import Empty from 'components/common/Empty';
import PageLoader from 'components/common/PageLoader';
import usersIcon from 'assets/images/users.svg';
import coinsIcon from 'assets/images/coins.svg';
import userIcon from 'assets/images/user-notif.svg';
import getCurrentOrg from 'helpers/getCurrentOrg';
import calendarIcon from 'assets/images/calendar-days.svg';
import walletIcon from 'assets/images/wallet_icon_dark.svg';
import formatNumber from 'helpers/formatNumber';
import ApproveWalletTransactionModal from 'components/common/modals/ApproveWalletTransactionModal';

const walletTopUpApprovalTypes = [
  'load_wallet_admin_approval',
  'user_wallet_top_up_admin_approval',
  'group_wallet_top_up_admin_approval',
];

const NotificationList = ({ items, loading, onReadNotification }) => {
  const currentOrg = getCurrentOrg();
  const { push } = useHistory();
  const currentUserId = useSelector(
    ({
      user: {
        currentUser: { details },
      },
    }) => details?.id,
  );
  const [approvalModal, setApprovalModal] = useState({
    open: false,
    requestId: null,
    notification: null,
  });

  if (loading) {
    return <PageLoader loaderContent="Loading notifications..." />;
  }

  if (Array.isArray(items) && items.length === 0) {
    return <Empty message="No notifications" icon="info circle" />;
  }

  const handleNotificationClick = item => {
    if (!item.read) {
      onReadNotification(item.request_id);
    }
    if (walletTopUpApprovalTypes.includes(item.type)) {
      setApprovalModal({
        open: true,
        requestId: item.request_id,
        notification: item,
      });
    } else {
      push(
        `/organizations/${currentOrg?.id}/requests/${item.request_id}`,
      );
    }
  };

  const handleCloseApprovalModal = () => {
    setApprovalModal({
      open: false,
      requestId: null,
      notification: null,
    });
  };

  return (
    <div className="notification-list">
      <ApproveWalletTransactionModal
        open={approvalModal.open}
        onClose={handleCloseApprovalModal}
        requestId={approvalModal.requestId}
        notification={approvalModal.notification}
        canApprove={
          Number(approvalModal.notification?.receiver_user_id) ===
          Number(currentUserId)
        }
      />
      <div className="notification-list__items">
        {(items ?? []).map(item => (
          <div
            className={`notification-list__items__item${
              !item.read
                ? ' notification-list__items__item--unread'
                : ''
            }`}
            key={item.id}
            tabIndex="-1"
            onKeyDown={null}
            role="button"
            onClick={() => handleNotificationClick(item)}
          >
            <div className="notification-list__items__item__details">
              <div className="notification-list__items__item__details__info">
                <div
                  className={`notification-list__items__item__details__info__name ${
                    !item.read ? 'unread' : ''
                  }`}
                >
                  {item.message.split(/(#\d+)/).map(part => {
                    const match = part.match(/^#(\d+)$/);
                    if (match) {
                      return (
                        <Link
                          key={item.request_id}
                          to={`/organizations/${currentOrg?.id}/requests/${item.request_id}`}
                        >
                          {part}
                        </Link>
                      );
                    }
                    return part;
                  })}
                </div>
                <div
                  className={`notification-list__items__item__details__info__numbers${
                    walletTopUpApprovalTypes.includes(item.type)
                      ? ' notification-list__items__item__details__info__numbers--wallet'
                      : ''
                  }`}
                >
                  {walletTopUpApprovalTypes.includes(item.type) ? (
                    <div className="number-group">
                      <Image
                        className="number-group__icon"
                        width={13}
                        src={walletIcon}
                        style={{ color: '#029B5D' }}
                      />
                      <span>
                        <span>
                          Wallet:{' '}
                          {item?.request?.payload?.[0]?.wallet_id ??
                            '—'}
                        </span>
                      </span>
                    </div>
                  ) : (
                    <div className="number-group">
                      {item?.request?.type !== 'group_micro_loan' ? (
                        <Image
                          className="number-group__icon"
                          width={13}
                          src={userIcon}
                        />
                      ) : (
                        <Image
                          className="number-group__icon"
                          width={13}
                          src={usersIcon}
                        />
                      )}
                      <span>
                        <span>
                          {item?.request?.type === 'group_micro_loan'
                            ? item?.group?.name
                            : `${item?.request?.user?.first_name} ${item?.request?.user?.last_name}`}
                        </span>
                      </span>
                    </div>
                  )}
                  {walletTopUpApprovalTypes.includes(item.type) && (
                    <div className="number-group">
                      {item?.initiator ? (
                        <>
                          <Image
                            className="number-group__icon"
                            width={13}
                            src={userIcon}
                          />
                          <span>
                            <span>
                              Initiated by:{' '}
                              {item.initiator.first_name}{' '}
                              {item.initiator.last_name}
                            </span>
                          </span>
                        </>
                      ) : null}
                    </div>
                  )}
                  <div className="number-group">
                    <Image
                      className="number-group__icon"
                      width={13}
                      src={coinsIcon}
                    />
                    <span>
                      <span>
                        Rwf {formatNumber(item?.request?.amount)}
                      </span>
                    </span>
                  </div>
                  <div className="number-group">
                    <Image
                      className="number-group__icon"
                      width={13}
                      src={calendarIcon}
                    />
                    <span>
                      <span>
                        {moment(item.created_at).format('MM/DD/YYYY')}
                      </span>
                    </span>
                  </div>

                  <div className="number-group">
                    {walletTopUpApprovalTypes.includes(item.type) ? (
                      (() => {
                        const status =
                          item?.request?.status ||
                          item?.request?.admins_approvals?.[0]
                            ?.status;
                        if (
                          status === 'accepted' ||
                          status === 'approved'
                        ) {
                          return (
                            <Label
                              style={{
                                color: '#029B5D',
                                backgroundColor:
                                  'rgba(76, 208, 128, 0.13)',
                              }}
                            >
                              APPROVED
                            </Label>
                          );
                        }
                        if (
                          status === 'rejected' ||
                          status === 'declined'
                        ) {
                          return (
                            <Label
                              style={{
                                color: '#D32F2F',
                                backgroundColor:
                                  'rgba(211, 47, 47, 0.1)',
                              }}
                            >
                              REJECTED
                            </Label>
                          );
                        }
                        return (
                          <Label
                            style={{
                              color: '#E67E22',
                              backgroundColor:
                                'rgba(230, 126, 34, 0.1)',
                            }}
                          >
                            PENDING
                          </Label>
                        );
                      })()
                    ) : (
                      <Label
                        style={{
                          color: '#029B5D',
                          backgroundColor: 'rgba(76, 208, 128, 0.13)',
                        }}
                      >
                        {item?.request?.admins_approvals?.reduce(
                          (prev, curr) => {
                            if (curr.status === 'accepted') {
                              return prev + 1;
                            }
                            return prev;
                          },
                          0,
                        ) || 0}
                        /3 approvals
                      </Label>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

NotificationList.propTypes = {
  items: PropTypes.arrayOf(Object).isRequired,
  loading: PropTypes.bool,
  onReadNotification: PropTypes.func,
};

NotificationList.defaultProps = {
  loading: false,
  onReadNotification: () => null,
};

export default NotificationList;
